import { useQuery } from 'react-query'
import { useParams } from 'react-router'
import { RouteParameters } from '../../constants'
import { Reservation } from '../../types'
import { logFactory } from '../../util/logFactory'
import { useAppointmentsService } from '../services'
import { createContext, FC, useContext } from 'react'

const hookName = 'useReservation'
const log = logFactory(hookName)

interface UseReservationContext {
  reservation?: Reservation
  isError: boolean
  isLoading: boolean
}

const defaultContext: UseReservationContext = {
  reservation: undefined,
  isError: false,
  isLoading: false,
}

const context = createContext<UseReservationContext>(defaultContext)

const { Provider } = context

const ReservationProvider: FC = ({ children }) => {
  const { [RouteParameters.RESERVATION_ID]: reservationId } = useParams()
  const { getReservation, isReady } = useAppointmentsService()

  const fetchReservationData = async (): Promise<Reservation | undefined> => {
    log(`🗓️ Fetching reservation...`, reservationId)

    const found = await getReservation(reservationId as string)

    log(`🗓️ Found reservation`, found)

    return found
  }

  const isQueryEnabled = Boolean(reservationId && isReady)

  const {
    data: reservation,
    isError,
    isLoading,
  } = useQuery([hookName, reservationId], fetchReservationData, {
    enabled: isQueryEnabled,
  })

  const contextValue: UseReservationContext = {
    reservation,
    isError,
    isLoading: (!isReady || isLoading) && !reservation,
  }

  return <Provider value={contextValue}>{children}</Provider>
}

const useReservation = (): UseReservationContext => useContext(context)

export { ReservationProvider, useReservation, hookName as useReservationHookName }
